/**
 * object-fit: contain rect inside a CSS box for intrinsic video size.
 */
export function containRect(boxW, boxH, videoW, videoH) {
  const vw = Math.max(1, videoW || boxW);
  const vh = Math.max(1, videoH || boxH);
  const scale = Math.min(boxW / vw, boxH / vh);
  const w = vw * scale;
  const h = vh * scale;
  return {
    x: (boxW - w) / 2,
    y: (boxH - h) / 2,
    w,
    h,
  };
}

/** object-fit: cover rect. Origin can be negative where the frame is cropped. */
export function coverRect(boxW, boxH, videoW, videoH) {
  const vw = Math.max(1, videoW || boxW);
  const vh = Math.max(1, videoH || boxH);
  const scale = Math.max(boxW / vw, boxH / vh);
  const w = vw * scale;
  const h = vh * scale;
  return {
    x: (boxW - w) / 2,
    y: (boxH - h) / 2,
    w,
    h,
  };
}

export function objectFitRect(boxW, boxH, videoW, videoH, fit = "contain") {
  return fit === "cover"
    ? coverRect(boxW, boxH, videoW, videoH)
    : containRect(boxW, boxH, videoW, videoH);
}

/**
 * Layout-pixel point → 0–1 video frame.
 * `fit` must match the <video> object-fit, or the box lands on the wrong pixels.
 */
export function localPointToVideoNorm(
  localX,
  localY,
  boxW,
  boxH,
  videoW,
  videoH,
  fit = "contain"
) {
  const rect = objectFitRect(boxW, boxH, videoW, videoH, fit);
  const x = (localX - rect.x) / Math.max(1e-6, rect.w);
  const y = (localY - rect.y) / Math.max(1e-6, rect.h);
  return {
    x: Math.min(1, Math.max(0, x)),
    y: Math.min(1, Math.max(0, y)),
    inside: x >= -0.02 && x <= 1.02 && y >= -0.02 && y <= 1.02,
    rect,
  };
}

/** Video-frame 0–1 box → rectangle in the same layout pixels as object-fit. */
export function videoNormToFitRect(roi, boxW, boxH, videoW, videoH, fit = "contain") {
  const rect = objectFitRect(boxW, boxH, videoW, videoH, fit);
  const x = Number(roi?.x) || 0;
  const y = Number(roi?.y) || 0;
  const w = Number(roi?.w) || 0;
  const h = Number(roi?.h) || 0;
  return {
    x: rect.x + x * rect.w,
    y: rect.y + y * rect.h,
    width: w * rect.w,
    height: h * rect.h,
  };
}

/**
 * Map a pointer event on a stage element into video-normalized 0–1 coords
 * using the same object-fit:contain letterbox as the <video>.
 * Prefer pointerToSvgViewBoxNorm when the hit target is an SVG inside
 * zoom/rotate transforms — getScreenCTM handles those correctly.
 */
export function pointerToVideoNorm(event, stageEl, videoWidth, videoHeight) {
  if (!stageEl) return { x: 0, y: 0, inside: false };
  const stage = stageEl.getBoundingClientRect();
  const boxW = Math.max(1, stage.width);
  const boxH = Math.max(1, stage.height);
  const rect = containRect(boxW, boxH, videoWidth, videoHeight);
  const x = (event.clientX - stage.left - rect.x) / Math.max(1e-6, rect.w);
  const y = (event.clientY - stage.top - rect.y) / Math.max(1e-6, rect.h);
  return {
    x: Math.min(1, Math.max(0, x)),
    y: Math.min(1, Math.max(0, y)),
    inside: x >= -0.02 && x <= 1.02 && y >= -0.02 && y <= 1.02,
    layout: {
      left: rect.x,
      top: rect.y,
      width: rect.w,
      height: rect.h,
    },
  };
}

/**
 * Map pointer → SVG viewBox units via getScreenCTM (accounts for CSS
 * zoom/pan/rotate/flip on the SVG and ancestors).
 * Use with viewBox="0 0 1 1" covering the video contain box.
 */
export function pointerToSvgViewBoxNorm(event, svgEl) {
  if (!elHasSvgPoint(svgEl)) {
    return { x: 0, y: 0, inside: false };
  }
  const ctm = svgEl.getScreenCTM();
  if (!ctm) return { x: 0, y: 0, inside: false };
  const pt = svgEl.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const local = pt.matrixTransform(ctm.inverse());
  const x = local.x;
  const y = local.y;
  return {
    x: Math.min(1, Math.max(0, x)),
    y: Math.min(1, Math.max(0, y)),
    inside: x >= -0.02 && x <= 1.02 && y >= -0.02 && y <= 1.02,
  };
}

function elHasSvgPoint(svgEl) {
  return Boolean(svgEl && typeof svgEl.createSVGPoint === "function");
}

/**
 * Legacy drawings were fractions of the full player box (letterbox included).
 * Map them into the video picture so a frame-sized canvas can still show them.
 */
export function stageNormToFramePx(nx, ny, stageW, stageH, videoW, videoH) {
  const rect = containRect(stageW, stageH, videoW, videoH);
  return {
    x: nx * stageW - rect.x,
    y: ny * stageH - rect.y,
  };
}

/** Map normalized video coords to pixel coords inside a contain rect. */
export function toContainPx(normX, normY, rect) {
  return {
    x: rect.x + normX * rect.w,
    y: rect.y + normY * rect.h,
  };
}

/**
 * Map pointer → video-normalized coords using the same model as <video>:
 * full element box + object-fit contain, with CSS rotate/flip on the element.
 */
export function pointerToContainNorm(event, svgEl, videoWidth, videoHeight) {
  return pointerToFitNorm(event, svgEl, videoWidth, videoHeight, "contain");
}

/**
 * Map a pointer into video-frame 0–1 coords.
 * The SVG must share the <video> layout box and CSS transform.
 * `fit` is the video's object-fit (`contain` or `cover`).
 */
export function pointerToFitNorm(
  event,
  svgEl,
  videoWidth,
  videoHeight,
  fit = "contain"
) {
  if (!svgEl || typeof svgEl.createSVGPoint !== "function") {
    return { x: 0, y: 0, inside: false };
  }
  const ctm = svgEl.getScreenCTM();
  if (!ctm) return { x: 0, y: 0, inside: false };
  const vb = svgEl.viewBox?.baseVal;
  const boxW = Math.max(1, vb?.width || svgEl.clientWidth || 1);
  const boxH = Math.max(1, vb?.height || svgEl.clientHeight || 1);
  const pt = svgEl.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const local = pt.matrixTransform(ctm.inverse());
  const hit = localPointToVideoNorm(
    local.x,
    local.y,
    boxW,
    boxH,
    videoWidth,
    videoHeight,
    fit
  );
  return {
    x: hit.x,
    y: hit.y,
    inside: hit.inside,
    layout: {
      left: hit.rect.x,
      top: hit.rect.y,
      width: hit.rect.w,
      height: hit.rect.h,
    },
  };
}

/**
 * Capture the currently displayed <video> frame as a JPEG data URL.
 * Uses intrinsic pixels (element letterbox space).
 * `maxEdge` shrinks the longest side so a phone frame stays a small upload.
 */
export function captureVideoFrameJpeg(videoEl, quality = 0.82, maxEdge = 0) {
  if (!videoEl) return null;
  let w = videoEl.videoWidth | 0;
  let h = videoEl.videoHeight | 0;
  if (w < 2 || h < 2) return null;
  const cap = Number(maxEdge) || 0;
  if (cap > 0 && Math.max(w, h) > cap) {
    const scale = cap / Math.max(w, h);
    w = Math.max(2, Math.round(w * scale));
    h = Math.max(2, Math.round(h * scale));
  }
  try {
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(videoEl, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", quality);
  } catch {
    return null;
  }
}

/** CSS box for an overlay layer matching object-fit:contain. */
export function containLayoutStyle(stageW, stageH, videoWidth, videoHeight) {
  const rect = containRect(
    Math.max(1, stageW),
    Math.max(1, stageH),
    videoWidth,
    videoHeight
  );
  return {
    position: "absolute",
    left: `${rect.x}px`,
    top: `${rect.y}px`,
    width: `${rect.w}px`,
    height: `${rect.h}px`,
  };
}
